import type { EnvironmentConfig } from '@luna/env';
import { getEnv } from '@luna/env';
import { createQuery, request } from '@luna/api';
import { createQueryString } from '@luna/router';

export type RecentTransactionTimes = {
    [accountId: string]: number;
};

type QueryParams = {
    accountId?: string;
};

type QueryResponse = {
    accounts: {
        [accountId: string]: string;
    };
};

const [, { queryApi }] = createQuery({
    name: '@transactions/recent-transaction-times',
    cacheDuration: 0,
    payloadCreator,
});

export const queryForRecentTransactionTimes = queryApi;

/**
 * Queries the `my/state` service for the times of recent transactions on
 * accounts the user has access to, or the account specified by `accountId`.
 *
 * A recent transaction is any that was recorded within the time of the
 * service's configured caching window. This is currently set to 2 hours.
 *
 * The service will respond with an object of IDs for accounts with recent
 * transactions mapped against the timestamp of the latest recent transaction
 * for the account.
 *
 * See the below documentation for more nuances of this service (eg: external
 * bank account errors).
 * @see https://gapi-spec.g-labs.io/#/state/StateResource_getStateReport_get
 */
async function payloadCreator({ accountId }: QueryParams): Promise<RecentTransactionTimes> {
    const url = getUrl(accountId);
    const { accounts } = await request<QueryResponse>(url);
    return Object.entries(accounts ?? {}).reduce(
        (entries, [id, timestamp]) => ({ ...entries, [id]: Number(timestamp) }),
        {},
    );
}

function getUrl(accountId?: string): string {
    const params: QueryParams = {
        ...(accountId && { accountId }),
    };
    const { georgeApiUrl } = getEnv<EnvironmentConfig>();
    const query = createQueryString(params);
    return `${georgeApiUrl}my/state${query}`;
}
